import React, { useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  Add,
  ArrowBack,
  Check,
  Close,
  DoneAll,
  LocalPharmacy,
  QrCodeScanner,
  Remove,
  ReportProblem,
  RestartAlt,
  VpnKey,
  WarningAmber,
} from '@mui/icons-material'
import API from '../../../services/API'
import { GlobalContext } from '../../../App'
import ExpiryDatePicker from '../../../components/DateTime/ExpiryDatePicker'
import { useDocumentTitle } from '../../../helpers/DocumentTitleManager'
import QRCodeReader from '../../../helpers/QRCodeReader'
import { parseSealScan, sealsMatch } from '../../../helpers/sealScan'

const cardSx = {
  p: 2,
  borderRadius: 2,
  border: '1px solid rgba(15,23,42,0.08)',
  backgroundColor: '#fff',
}

const isExpired = (expiry) =>
  !!expiry && moment(expiry).endOf('day').isBefore(moment().endOf('day'))
const isExpiringSoon = (expiry) =>
  !!expiry &&
  !isExpired(expiry) &&
  moment(expiry).isBefore(moment().add(30, 'days'))

// A line is one medicine + batch + expiry: the same batch number can be held
// with two expiry dates and they must be counted separately. Mirrors
// batchKey() in MedicineDrawer.jsx / helpers/medsMx/affectedMeds.js.
const lineKey = (medicineId, batch, expiry) =>
  `${medicineId}|${batch ?? ''}|${
    expiry ? moment(expiry).format('YYYY-MM-DD') : 'no-expiry'
  }`

const StatTile = ({ icon: Icon, label, value, tone = '#242F40' }) => (
  <Paper
    elevation={0}
    sx={{
      px: 1.5,
      py: 1,
      borderRadius: 2,
      border: '1px solid rgba(15,23,42,0.08)',
      minWidth: 118,
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center">
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: 1.5,
          display: 'grid',
          placeItems: 'center',
          color: tone,
          backgroundColor: `${tone}18`,
          flexShrink: 0,
        }}
      >
        <Icon fontSize="small" />
      </Box>
      <Stack minWidth={0}>
        <Typography fontWeight={950} fontSize="1.1rem" lineHeight={1}>
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {label}
        </Typography>
      </Stack>
    </Stack>
  </Paper>
)

const YesNo = ({
  label,
  value,
  onChange,
  yesValue = true,
  noValue = false,
}) => (
  <Stack gap={0.75}>
    <Typography variant="body2" fontWeight={850}>
      {label}
    </Typography>
    <ToggleButtonGroup
      exclusive
      size="small"
      value={value}
      onChange={(e, next) => next !== null && onChange(next)}
      sx={{
        '& .MuiToggleButton-root': {
          px: 3,
          textTransform: 'none',
          fontWeight: 850,
        },
      }}
    >
      <ToggleButton value={yesValue}>Yes</ToggleButton>
      <ToggleButton value={noValue}>No</ToggleButton>
    </ToggleButtonGroup>
  </Stack>
)

const StateButton = ({ active, tone, title, onClick, children }) => (
  <Tooltip title={title} placement="top">
    <IconButton
      size="small"
      aria-label={title}
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
      sx={{
        border: '1px solid',
        borderColor: active ? tone : 'divider',
        backgroundColor: active ? `${tone}1F` : 'transparent',
        color: active ? tone : 'action.disabled',
        '&:hover': { backgroundColor: `${tone}14`, color: tone },
      }}
    >
      {children}
    </IconButton>
  </Tooltip>
)

// One line to check. Clicking the row cycles unreviewed → correct →
// discrepancy → correct for fast tick-tick-tick auditing; the two buttons
// set a state directly. A discrepancy opens an inline "found" counter.
const AuditLine = ({ row, onSetCorrect, onSetActual }) => {
  const state = row.isCorrect
  const missing = row.quantity === 0
  const expired = isExpired(row.expiry)
  const soon = isExpiringSoon(row.expiry)
  const tone =
    state === true ? '#16A34A' : state === false ? '#DC2626' : undefined
  const needsCount = state === false && row.actualQuantity === row.quantity

  return (
    <Box
      onClick={() => onSetCorrect(row, state === true ? false : true)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        p: 1.25,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: tone ?? 'rgba(15,23,42,0.1)',
        backgroundColor: tone ? `${tone}0D` : '#fff',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
        '&:hover': { borderColor: tone ?? 'rgba(15,23,42,0.3)' },
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        gap={1.25}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
      >
        <Stack direction="row" gap={0.5} alignItems="center">
          <StateButton
            active={state === true}
            tone="#16A34A"
            title="Contents correct"
            onClick={() => onSetCorrect(row, true)}
          >
            <Check fontSize="small" />
          </StateButton>
          <StateButton
            active={state === false}
            tone="#DC2626"
            title="Record a discrepancy"
            onClick={() => onSetCorrect(row, false)}
          >
            <Close fontSize="small" />
          </StateButton>
        </Stack>

        <Stack minWidth={0} flex={1}>
          <Typography fontWeight={900} lineHeight={1.2}>
            {row.title}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {[row.constitution, row.presentation].filter(Boolean).join(' · ')}
          </Typography>
        </Stack>

        <Stack
          direction="row"
          gap={0.75}
          flexWrap="wrap"
          alignItems="center"
          justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
        >
          {missing ? (
            <Chip
              size="small"
              color="error"
              variant="outlined"
              label="Not in bag"
              sx={{ fontWeight: 800 }}
            />
          ) : (
            <>
              <Chip
                size="small"
                variant="outlined"
                label={`Batch ${row.batch}`}
                sx={{ fontWeight: 800 }}
              />
              <Chip
                size="small"
                icon={expired ? <WarningAmber /> : undefined}
                label={
                  row.expiry
                    ? `${expired ? 'Expired ' : 'Exp '}${moment(row.expiry).format('DD/MM/YYYY')}`
                    : 'No expiry'
                }
                color={expired ? 'error' : soon ? 'warning' : 'default'}
                variant={expired || soon ? 'filled' : 'outlined'}
                sx={{ fontWeight: 800 }}
              />
            </>
          )}
          <Typography
            fontWeight={950}
            sx={{ minWidth: 44, textAlign: 'right' }}
          >
            × {row.quantity}
          </Typography>
        </Stack>
      </Stack>

      {state === false && (
        <Stack
          direction="row"
          gap={1}
          alignItems="center"
          flexWrap="wrap"
          onClick={(e) => e.stopPropagation()}
          sx={{
            pl: { sm: 9.5 },
            pt: 1,
            borderTop: '1px dashed rgba(220,38,38,0.35)',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Expected <strong>{row.quantity}</strong> · found
          </Typography>
          <IconButton
            size="small"
            aria-label="One fewer"
            onClick={() =>
              onSetActual(row, Math.max(0, row.actualQuantity - 1))
            }
            disabled={row.actualQuantity <= 0}
          >
            <Remove fontSize="small" />
          </IconButton>
          <TextField
            size="small"
            type="number"
            value={row.actualQuantity}
            onChange={(e) =>
              onSetActual(row, Math.max(0, Number(e.target.value) || 0))
            }
            onFocus={(e) => e.target.select()}
            inputProps={{
              min: 0,
              'aria-label': `Quantity of ${row.title} found`,
              style: { textAlign: 'center', width: 48, padding: '6px 4px' },
            }}
          />
          <IconButton
            size="small"
            aria-label="One more"
            onClick={() => onSetActual(row, row.actualQuantity + 1)}
          >
            <Add fontSize="small" />
          </IconButton>
          {needsCount ? (
            <Typography variant="caption" color="error.main" fontWeight={800}>
              Enter the quantity actually found
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">
              {row.actualQuantity < row.quantity
                ? `${row.quantity - row.actualQuantity} short`
                : `${row.actualQuantity - row.quantity} extra`}
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  )
}

const Audit = () => {
  const { bagID } = useParams()
  const navigate = useNavigate()
  const { setToast } = useContext(GlobalContext)
  const {setPageTitle} = useDocumentTitle()

  const [loading, setLoading] = useState(true)
  const [location, setLocation] = useState({})
  const [contents, setContents] = useState([])
  const [requiredDrugs, setRequiredDrugs] = useState([])
  const [medicineRows, setMedicineRows] = useState([])

  const [tagRemoved, setTagRemoved] = useState(null) // 'Yes' | 'No' | null
  const [matchCode, setMatchCode] = useState(null) // true | false | null
  const [completeFullAudit, setCompleteFullAudit] = useState(false)
  const [filter, setFilter] = useState('all') // all | unreviewed | discrepancy
  const [newTag, setNewTag] = useState('')
  const [leaveUntagged, setLeaveUntagged] = useState(false)
  // QR seal tags: 'verify' scans the fitted seal against the record, 'newTag'
  // scans the replacement seal into the reseal field. The scanned flags are
  // provenance on the audit - cleared if the answer/number is changed by hand.
  const [scanMode, setScanMode] = useState(null) // 'verify' | 'newTag' | null
  const [originalTagScanned, setOriginalTagScanned] = useState(false)
  const [newTagScanned, setNewTagScanned] = useState(false)
  const [verifyScan, setVerifyScan] = useState(null) // last scanned seal code
  const [reportIncident, setReportIncident] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const loadPageAPI = () => {
    API.get(`/medicines-management/location?id=${bagID}&logs=false`)
      .then((res) => {
        setLocation(res.data.location)
        setContents(res.data.contents)
        setRequiredDrugs(res.data.requiredDrugs)
      })
      .catch((e) => console.log(e))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadPageAPI()
  }, [])

  useEffect(() => {
    if (location?.name) {
      setPageTitle(location?.name + ' Audit')
    }
  }, [location?.name, setPageTitle])

  useEffect(() => {
    if (location?.tagNumber) {
      setTagRemoved(location.tagNumber === 'Removed' ? 'Yes' : null)
    }
  }, [location])

  // One row per medicine + batch + expiry, plus a "not in bag" row for every
  // required drug with no stock at all.
  useEffect(() => {
    if (!contents || !requiredDrugs) return
    const rowMap = new Map()
    for (const item of contents) {
      const key = lineKey(item.medicine._id, item.batchNumber, item.expiry)
      const existing = rowMap.get(key)
      if (existing) {
        existing.quantity++
        existing.actualQuantity++
      } else {
        rowMap.set(key, {
          _id: item.medicine._id,
          title: item.medicine.title,
          constitution: item.medicine.constitution,
          presentation: item.medicine.presentation,
          batch: item.batchNumber,
          expiry: item.expiry,
          quantity: 1,
          actualQuantity: 1,
          isCorrect: null,
          uniqueID: key,
        })
      }
    }
    const stocked = new Set([...rowMap.values()].map((row) => row._id))
    for (const required of requiredDrugs) {
      if (stocked.has(required._id)) continue
      rowMap.set(`missing-${required._id}`, {
        _id: required._id,
        title: required.title,
        constitution: required.constitution,
        presentation: required.presentation,
        batch: '—',
        expiry: null,
        quantity: 0,
        actualQuantity: 0,
        isCorrect: null,
        uniqueID: `missing_${required._id}`,
      })
    }
    setMedicineRows(
      [...rowMap.values()].sort(
        (a, b) =>
          a.title.localeCompare(b.title) ||
          (a.expiry ? moment(a.expiry).valueOf() : Infinity) -
            (b.expiry ? moment(b.expiry).valueOf() : Infinity),
      ),
    )
  }, [contents, requiredDrugs])

  const updateRow = (uniqueID, patch) =>
    setMedicineRows((prev) =>
      prev.map((row) =>
        row.uniqueID === uniqueID
          ? { ...row, ...(typeof patch === 'function' ? patch(row) : patch) }
          : row,
      ),
    )

  // Marking a row correct resets the count to what's expected; marking a
  // discrepancy keeps whatever the auditor has counted so far.
  const setRowCorrect = (row, isCorrect) =>
    updateRow(row.uniqueID, (current) => ({
      isCorrect,
      actualQuantity:
        isCorrect === false ? current.actualQuantity : current.quantity,
    }))
  const setRowActual = (row, actualQuantity) =>
    updateRow(row.uniqueID, { actualQuantity })

  // Bulk: every unreviewed line becomes correct. Discrepancies the auditor
  // has already flagged are deliberately left alone.
  const markAllChecked = () =>
    setMedicineRows((prev) =>
      prev.map((row) =>
        row.isCorrect === null
          ? { ...row, isCorrect: true, actualQuantity: row.quantity }
          : row,
      ),
    )
  const clearAll = () =>
    setMedicineRows((prev) =>
      prev.map((row) => ({
        ...row,
        isCorrect: null,
        actualQuantity: row.quantity,
      })),
    )

  const expired = useMemo(
    () => (contents ?? []).filter((item) => isExpired(item.expiry)),
    [contents],
  )
  const reviewedCount = medicineRows.filter((r) => r.isCorrect !== null).length
  const unreviewedCount = medicineRows.length - reviewedCount
  const flaggedRows = medicineRows.filter((row) => row.isCorrect === false)
  const discrepancyRows = flaggedRows.filter(
    (row) => row.actualQuantity !== row.quantity,
  )
  const shortageRows = discrepancyRows.filter(
    (row) => row.actualQuantity < row.quantity && row.batch !== '—',
  )
  const surplusRows = discrepancyRows.filter(
    (row) => row.actualQuantity > row.quantity,
  )

  const visibleRows = medicineRows.filter((row) =>
    filter === 'unreviewed'
      ? row.isCorrect === null
      : filter === 'discrepancy'
        ? row.isCorrect === false
        : true,
  )

  // Surplus lines need a confirmed batch + expiry before they are added
  const [additionDetails, setAdditionDetails] = useState({})
  useEffect(() => {
    setAdditionDetails((prev) => {
      const next = {}
      for (const row of surplusRows) {
        next[row.uniqueID] = prev[row.uniqueID] ?? {
          batch: row.batch !== '—' ? row.batch : '',
          expiry: row.expiry ?? null,
        }
      }
      return next
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medicineRows])
  const updateAdditionDetails = (uniqueID, patch) =>
    setAdditionDetails((prev) => ({
      ...prev,
      [uniqueID]: { ...prev[uniqueID], ...patch },
    }))

  // Batch/expiry of this drug already in stock anywhere, offered as options
  const [batchOptions, setBatchOptions] = useState({})
  const surplusDescriptionIds = [...new Set(surplusRows.map((x) => x._id))]
    .sort()
    .join(',')
  useEffect(() => {
    if (!surplusDescriptionIds) return
    API.get(`/medsmx/batch-options?ids=${surplusDescriptionIds}`)
      .then((res) => {
        if (res.status === 200) setBatchOptions(res.data)
      })
      .catch((e) => console.log(e))
  }, [surplusDescriptionIds])

  const additionsIncomplete = surplusRows.some((row) => {
    const details = additionDetails[row.uniqueID]
    return !details?.batch || !moment(details?.expiry).isValid()
  })

  const isBag = location?.type !== 'Medicine Store'
  const sealIntact = location?.tagNumber && location.tagNumber !== 'Removed'
  const showSealStep = sealIntact && expired.length === 0
  const quickComplete =
    showSealStep &&
    tagRemoved === 'No' &&
    matchCode === true &&
    !completeFullAudit
  const fullAudit =
    !showSealStep ||
    tagRemoved === 'Yes' ||
    matchCode === false ||
    completeFullAudit

  const blockingReason =
    unreviewedCount > 0
      ? `${unreviewedCount} line${unreviewedCount === 1 ? '' : 's'} still to review`
      : newTag.length < 1
        ? 'Record the new seal number, or leave the bag untagged'
        : flaggedRows.some((x) => x.quantity === x.actualQuantity)
          ? 'Enter the quantity found for each discrepancy'
          : additionsIncomplete
            ? 'Confirm the batch and expiry of medicines being added'
            : discrepancyRows.length > 0 && !reportIncident
              ? 'Decide whether to raise an incident'
              : ''

  const handleScan = (decodedText) => {
    const code = parseSealScan(decodedText)
    if (!code) return
    if (scanMode === 'verify') {
      setMatchCode(sealsMatch(code, location.tagNumber))
      setOriginalTagScanned(true)
      setVerifyScan(code)
    } else if (scanMode === 'newTag') {
      setNewTag(code)
      setLeaveUntagged(false)
      setNewTagScanned(true)
    }
    setScanMode(null)
  }

  const handleAuditSubmit = (e) => {
    e.preventDefault()
    if (blockingReason || submitting) return
    setSubmitting(true)
    API.post('/medsmx/audit', {
      location: location._id,
      originalTag: location.tagNumber,
      newTag: leaveUntagged ? 'Removed' : newTag,
      tagNumberMatch: matchCode,
      originalTagScanned,
      newTagScanned: newTagScanned && !leaveUntagged,
      bagOpened: tagRemoved === 'Yes',
      discrepancies: flaggedRows,
      medicines: medicineRows,
      stockChanges: {
        removals: shortageRows.map((row) => ({
          medicineDescription: row._id,
          title: row.title,
          batch: row.batch,
          expiry: row.expiry,
          qty: row.quantity - row.actualQuantity,
        })),
        additions: surplusRows.map((row) => {
          const details = additionDetails[row.uniqueID] ?? {}
          return {
            medicineDescription: row._id,
            title: row.title,
            batch: details.batch,
            expiry: details.expiry,
            qty: row.actualQuantity - row.quantity,
          }
        }),
      },
      ...(discrepancyRows.length > 0
        ? { reportIncident: reportIncident === 'Yes' }
        : {}),
    })
      .then((res) => {
        if (res.status === 200) {
          navigate('/checkmedsbag/' + location._id)
          const stockNote =
            discrepancyRows.length > 0 ? ' — bag contents updated' : ''
          const incidentNote = res.data?.incidentNumber
            ? `. Incident #${res.data.incidentNumber} raised`
            : ''
          setToast({ message: `Audit complete${stockNote}${incidentNote}` })
        } else {
          setToast({ message: 'Unable to submit the audit', severity: 'error' })
        }
      })
      .catch((e) => {
        console.log(e)
        setToast({ message: 'Unable to submit the audit', severity: 'error' })
      })
      .finally(() => setSubmitting(false))
  }

  const handleMarkAuditComplete = () => {
    if (submitting) return
    setSubmitting(true)
    API.post('/medsmx/audit', {
      location: location._id,
      bagOpened: false,
      tagNumberMatch: matchCode,
      originalTagScanned,
      discrepancies: false,
      medicines: medicineRows,
      originalTag: location.tagNumber,
      newTag: 'Unchanged',
    })
      .then((res) => {
        if (res.status === 200) {
          navigate('/checkmedsbag/' + location._id)
          setToast({ message: 'Audit complete' })
        } else {
          setToast({ message: 'Unable to submit the audit', severity: 'error' })
        }
      })
      .catch((e) => console.log(e))
      .finally(() => setSubmitting(false))
  }

  const progress =
    medicineRows.length > 0 ? (reviewedCount / medicineRows.length) * 100 : 0

  return (
    <Box
      component="form"
      onSubmit={handleAuditSubmit}
      display="flex"
      flexDirection="column"
      gap={2}
      sx={{ pb: 3, maxWidth: 1200, mx: 'auto', width: '100%' }}
    >
      {loading && <LinearProgress />}

      <Paper elevation={0} sx={cardSx}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
          gap={1.5}
        >
          <Stack direction="row" gap={1} alignItems="center" minWidth={0}>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                minWidth: 36,
                width: 36,
                height: 36,
                borderRadius: 1.5,
                p: 0,
              }}
            >
              <ArrowBack fontSize="small" />
            </Button>
            <Box minWidth={0}>
              <Typography variant="h5" fontWeight={950} noWrap>
                Audit {location.name}
              </Typography>
              <Stack direction="row" gap={0.75} flexWrap="wrap" useFlexGap>
                {location.type && (
                  <Chip
                    size="small"
                    label={location.type}
                    sx={{ borderRadius: 1, fontWeight: 850 }}
                  />
                )}
                {isBag && location.level && (
                  <Chip
                    size="small"
                    label={location.level}
                    sx={{ borderRadius: 1, fontWeight: 850 }}
                  />
                )}
                {isBag && (
                  <Chip
                    size="small"
                    icon={<VpnKey />}
                    label={
                      sealIntact ? `Seal ${location.tagNumber}` : 'Seal removed'
                    }
                    color={sealIntact ? 'default' : 'warning'}
                    sx={{ borderRadius: 1, fontWeight: 850 }}
                  />
                )}
              </Stack>
            </Box>
          </Stack>
          <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap>
            <StatTile
              icon={LocalPharmacy}
              label="Lines"
              value={medicineRows.length}
            />
            <StatTile
              icon={Check}
              label="Checked"
              value={`${reviewedCount}/${medicineRows.length}`}
              tone={
                medicineRows.length > 0 && reviewedCount === medicineRows.length
                  ? '#16A34A'
                  : '#2563EB'
              }
            />
            <StatTile
              icon={ReportProblem}
              label="Discrepancies"
              value={flaggedRows.length}
              tone={flaggedRows.length > 0 ? '#DC2626' : '#16A34A'}
            />
            <StatTile
              icon={WarningAmber}
              label="Expired"
              value={expired.length}
              tone={expired.length > 0 ? '#DC2626' : '#16A34A'}
            />
          </Stack>
        </Stack>
      </Paper>

      {showSealStep && (
        <Paper elevation={0} sx={cardSx}>
          <Stack gap={1.5}>
            <Box>
              <Typography fontWeight={950}>Seal check</Typography>
              <Typography variant="body2" color="text.secondary">
                Everything in this bag is in date, so if the seal is intact and
                matches the record there is nothing to open.
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={3}>
              <YesNo
                label="Has the seal been removed?"
                value={tagRemoved}
                onChange={setTagRemoved}
                yesValue="Yes"
                noValue="No"
              />
              {tagRemoved === 'No' && (
                <YesNo
                  label={`Does the seal read ${location.tagNumber}?`}
                  value={matchCode}
                  onChange={(value) => {
                    setMatchCode(value)
                    setOriginalTagScanned(false)
                    setVerifyScan(null)
                  }}
                />
              )}
            </Stack>
            {tagRemoved === 'No' && (
              <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<QrCodeScanner />}
                  onClick={() => setScanMode('verify')}
                  sx={{ textTransform: 'none', fontWeight: 850, width: 'fit-content' }}
                >
                  Scan the seal instead
                </Button>
                {originalTagScanned && verifyScan && (
                  <Chip
                    size="small"
                    color={matchCode ? 'success' : 'error'}
                    icon={matchCode ? <Check /> : <Close />}
                    label={
                      matchCode
                        ? `Scanned ${verifyScan} — verified`
                        : `Scanned ${verifyScan} — does not match ${location.tagNumber}`
                    }
                  />
                )}
              </Stack>
            )}
            {quickComplete && (
              <Alert
                severity="success"
                sx={{ alignItems: 'center' }}
                action={
                  <Stack direction="row" gap={1}>
                    <Button
                      size="small"
                      onClick={() => setCompleteFullAudit(true)}
                      disabled={submitting}
                    >
                      Full audit anyway
                    </Button>
                    <Button
                      size="small"
                      color="success"
                      variant="contained"
                      onClick={handleMarkAuditComplete}
                      disabled={submitting}
                    >
                      Mark audit complete
                    </Button>
                  </Stack>
                }
              >
                Seal intact and matching, all contents in date. You can mark
                this audit complete without opening the bag.
              </Alert>
            )}
            {tagRemoved === 'No' && matchCode === false && (
              <Alert severity="warning">
                The seal number doesn&apos;t match the record, so the bag needs
                a full check below.
              </Alert>
            )}
          </Stack>
        </Paper>
      )}

      {fullAudit && !loading && (
        <>
          <Paper elevation={0} sx={{ ...cardSx, p: 0, overflow: 'hidden' }}>
            <Stack gap={1.25} sx={{ p: 2, pb: 1.5 }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', md: 'center' }}
                gap={1}
              >
                <Box>
                  <Typography fontWeight={950}>Check the contents</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {tagRemoved === 'No' ? 'Remove the seal, then ' : ''}
                    mark each line{' '}
                    <Check
                      sx={{
                        fontSize: 16,
                        color: 'success.main',
                        verticalAlign: 'text-bottom',
                      }}
                    />{' '}
                    if it matches what&apos;s listed or{' '}
                    <Close
                      sx={{
                        fontSize: 16,
                        color: 'error.main',
                        verticalAlign: 'text-bottom',
                      }}
                    />{' '}
                    to record what you actually found.
                  </Typography>
                </Box>
                <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap>
                  <Tooltip
                    title={
                      unreviewedCount === 0
                        ? 'Every line has been reviewed'
                        : 'Marks every unreviewed line as correct. Discrepancies you have flagged are kept.'
                    }
                  >
                    <span>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<DoneAll />}
                        onClick={markAllChecked}
                        disabled={unreviewedCount === 0}
                        sx={{
                          borderRadius: 1.5,
                          textTransform: 'none',
                          fontWeight: 900,
                        }}
                      >
                        Mark all as checked
                        {unreviewedCount > 0 ? ` (${unreviewedCount})` : ''}
                      </Button>
                    </span>
                  </Tooltip>
                  <Button
                    startIcon={<RestartAlt />}
                    onClick={clearAll}
                    disabled={reviewedCount === 0}
                    sx={{
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 850,
                    }}
                  >
                    Clear
                  </Button>
                </Stack>
              </Stack>

              {expired.length > 0 && (
                <Alert severity="error" variant="outlined" sx={{ py: 0.5 }}>
                  This bag holds {expired.length} expired item
                  {expired.length === 1 ? '' : 's'}, so a full check is
                  required. Remove expired stock from the bag&apos;s page once
                  the audit is done.
                </Alert>
              )}

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
                gap={1}
              >
                <Stack flex={1} gap={0.5} minWidth={0}>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    color={
                      progress === 100
                        ? flaggedRows.length > 0
                          ? 'error'
                          : 'success'
                        : 'primary'
                    }
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {reviewedCount} of {medicineRows.length} checked
                    {flaggedRows.length > 0
                      ? ` · ${flaggedRows.length} discrepanc${flaggedRows.length === 1 ? 'y' : 'ies'}`
                      : ''}
                  </Typography>
                </Stack>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={filter}
                  onChange={(e, value) => value && setFilter(value)}
                  sx={{
                    '& .MuiToggleButton-root': {
                      px: 1.5,
                      textTransform: 'none',
                      fontWeight: 850,
                    },
                  }}
                >
                  <ToggleButton value="all">All</ToggleButton>
                  <ToggleButton value="unreviewed">
                    To review ({unreviewedCount})
                  </ToggleButton>
                  <ToggleButton value="discrepancy">
                    Discrepancies ({flaggedRows.length})
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </Stack>

            <Stack gap={1} sx={{ px: 2, pb: 2 }}>
              {visibleRows.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                  sx={{ py: 3 }}
                >
                  {medicineRows.length === 0
                    ? 'Nothing is recorded in this bag and nothing is required for its level.'
                    : filter === 'unreviewed'
                      ? 'Every line has been reviewed.'
                      : 'No discrepancies recorded.'}
                </Typography>
              ) : (
                visibleRows.map((row) => (
                  <AuditLine
                    key={row.uniqueID}
                    row={row}
                    onSetCorrect={setRowCorrect}
                    onSetActual={setRowActual}
                  />
                ))
              )}
            </Stack>
          </Paper>

          {discrepancyRows.length > 0 && (
            <Paper
              elevation={0}
              sx={{ ...cardSx, borderColor: 'rgba(124,58,237,0.4)' }}
            >
              <Stack gap={1.25}>
                <Box>
                  <Typography fontWeight={950} color="#7C3AED">
                    Update bag contents
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    The bag&apos;s recorded contents will be updated to match
                    your audit when you submit.
                  </Typography>
                </Box>
                {shortageRows.map((row) => (
                  <Alert
                    key={row.uniqueID}
                    severity="error"
                    variant="outlined"
                    icon={<Remove />}
                    sx={{ py: 0.5 }}
                  >
                    {row.quantity - row.actualQuantity} ×{' '}
                    <strong>{row.title}</strong> (batch {row.batch}
                    {row.expiry
                      ? `, exp ${moment(row.expiry).format('DD/MM/YYYY')}`
                      : ''}
                    ) will be removed from the bag.
                  </Alert>
                ))}
                {surplusRows.map((row) => {
                  const details = additionDetails[row.uniqueID] ?? {}
                  return (
                    <Box
                      key={row.uniqueID}
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        border: '1px solid rgba(22,163,74,0.5)',
                        backgroundColor: 'rgba(22,163,74,0.04)',
                      }}
                    >
                      <Stack gap={1}>
                        <Typography variant="body2">
                          <Add
                            sx={{
                              fontSize: 16,
                              color: 'success.main',
                              verticalAlign: 'text-bottom',
                            }}
                          />{' '}
                          {row.actualQuantity - row.quantity} ×{' '}
                          <strong>{row.title}</strong> will be added to the bag.
                          Confirm the batch and expiry:
                        </Typography>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          gap={1.5}
                        >
                          <Autocomplete
                            freeSolo
                            size="small"
                            options={batchOptions[row._id] ?? []}
                            getOptionLabel={(option) =>
                              typeof option === 'string'
                                ? option
                                : (option.batchNumber ?? '')
                            }
                            renderOption={(props, option) => (
                              <li
                                {...props}
                                key={option.batchNumber + option.expiry}
                              >
                                {option.batchNumber} — expires{' '}
                                {moment(option.expiry).format('DD/MM/YYYY')}
                              </li>
                            )}
                            inputValue={details.batch ?? ''}
                            onInputChange={(e, value) =>
                              updateAdditionDetails(row.uniqueID, {
                                batch: value,
                              })
                            }
                            onChange={(e, value) => {
                              if (value && typeof value !== 'string') {
                                updateAdditionDetails(row.uniqueID, {
                                  batch: value.batchNumber,
                                  expiry: value.expiry,
                                })
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Batch number"
                                error={!details.batch}
                              />
                            )}
                            sx={{ minWidth: 220, flex: 1 }}
                          />
                          <ExpiryDatePicker
                            label="Expiry (month/year)"
                            size="small"
                            date={details.expiry}
                            onChange={(value) =>
                              updateAdditionDetails(row.uniqueID, {
                                expiry: value?.isValid()
                                  ? value.toISOString()
                                  : null,
                              })
                            }
                            error={!moment(details.expiry).isValid()}
                          />
                        </Stack>
                      </Stack>
                    </Box>
                  )
                })}
              </Stack>
            </Paper>
          )}

          {discrepancyRows.length > 0 && (
            <Paper elevation={0} sx={cardSx}>
              <Stack gap={1.25}>
                <Box>
                  <Typography fontWeight={950}>Raise an incident?</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Discrepancies were found. An incident can be created
                    automatically, pre-filled with the audit details and linked
                    to this bag.
                  </Typography>
                </Box>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={reportIncident}
                  onChange={(e, value) => value && setReportIncident(value)}
                  sx={{
                    flexWrap: 'wrap',
                    '& .MuiToggleButton-root': {
                      textTransform: 'none',
                      fontWeight: 850,
                      px: 2,
                    },
                  }}
                >
                  <ToggleButton value="Yes">
                    Yes — create a pre-filled incident
                  </ToggleButton>
                  <ToggleButton value="No">
                    No — record the discrepancies only
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </Paper>
          )}

          <Paper elevation={0} sx={cardSx}>
            <Stack gap={1.5}>
              <Box>
                <Typography fontWeight={950}>Reseal and complete</Typography>
                <Typography variant="body2" color="text.secondary">
                  Record the seal fitted after the check, or leave the bag
                  untagged.
                </Typography>
              </Box>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', md: 'center' }}
                gap={1.5}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  gap={1.5}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                >
                  <TextField
                    value={leaveUntagged ? '' : newTag}
                    onChange={(e) => {
                      setNewTag(e.target.value)
                      setNewTagScanned(false)
                    }}
                    label="New seal number"
                    size="small"
                    autoComplete="off"
                    disabled={leaveUntagged}
                    sx={{ minWidth: 220 }}
                    helperText={
                      newTagScanned && !leaveUntagged
                        ? 'Recorded by scanning the seal'
                        : undefined
                    }
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<QrCodeScanner />}
                    onClick={() => setScanMode('newTag')}
                    disabled={leaveUntagged}
                    sx={{ textTransform: 'none', fontWeight: 850 }}
                  >
                    Scan seal
                  </Button>
                  <FormControlLabel
                    label="Leave untagged"
                    control={
                      <Checkbox
                        checked={leaveUntagged}
                        onChange={(e) => {
                          setLeaveUntagged(e.target.checked)
                          setNewTag(e.target.checked ? 'Removed' : '')
                          setNewTagScanned(false)
                        }}
                      />
                    }
                  />
                </Stack>
                <Tooltip title={blockingReason} placement="top" arrow>
                  <span>
                    <Button
                      color="success"
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={!!blockingReason || submitting}
                      sx={{
                        borderRadius: 1.5,
                        textTransform: 'none',
                        fontWeight: 900,
                      }}
                    >
                      {submitting ? 'Submitting…' : 'Submit audit'}
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
              {blockingReason && (
                <Typography variant="caption" color="text.secondary">
                  To submit: {blockingReason.toLowerCase()}.
                </Typography>
              )}
            </Stack>
          </Paper>
        </>
      )}

      <QRCodeReader
        open={scanMode !== null}
        onClose={() => setScanMode(null)}
        onScan={handleScan}
      />
    </Box>
  )
}

export default Audit
